import * as React from "react";
import { DataLayer } from "@grandvisionhq/analytics";
import { Util } from "@grandvisionhq/common";
import { useIntl } from "@grandvisionhq/state";
import { createScriptTag, injectTags, isNumber } from "@grandvisionhq/utils";
import { REVIEWS_EVENT_ID, REVIEW_SNIPPET_ID } from "./constants.js";
import { reviewsReducer, initialState } from "./reducer.js";
import { createPowerReviewsConfig, mergeConfigs } from "../../utils/utils/create-powerreviews-config.js";
const mapStateToPowerReviewsType = {
  reviewWrite: "Write",
  reviewSnippet: "ReviewSnippet",
  reviewDisplay: "ReviewDisplay"
};
const ReviewsContext = React.createContext({});
const useReviewsContext = () => React.useContext(ReviewsContext);
const PR_SCRIPT_ID = "power-reviews-script";
const PR_SCRIPT_SRC = "https://ui.powerreviews.com/stable/4.1/ui.js";
const ReviewsProvider = ({
  enabled,
  config,
  children
}) => {
  const [state, dispatch] = React.useReducer(reviewsReducer, initialState);
  const [reviewCount, setReviewCount] = React.useState(null);
  const [hasError, setHasError] = React.useState(false);
  const { locale } = useIntl();
  const widgets = React.useMemo(() => {
    return Object.keys(state).reduce((result, key) => {
      if (state[key] !== null) {
        const widgetConfig = createPowerReviewsConfig({
          config,
          locale,
          ...state[key],
          type: mapStateToPowerReviewsType[key]
        });
        widgetConfig.on_read_reviews_click = () => {
          const customEvent = Util.createCustomEvent(REVIEWS_EVENT_ID);
          window.dispatchEvent(customEvent);
          DataLayer.Events.pushDrawerEventToDataLayer({
            siteFeature: "reviews",
            interactionType: "customer_reviews_click"
          });
        };
        widgetConfig.on_render = function(_, data) {
          if (isNumber(data?.review_count)) {
            setReviewCount(data.review_count);
          }
          const writeReviewSnippet = document.getElementById(REVIEW_SNIPPET_ID);
          const writeReviewLink = writeReviewSnippet?.getElementsByClassName(
            "pr-snippet-write-review-link"
          )[0];
          writeReviewLink?.addEventListener("click", pushWriteReviewToDataLayer);
        };
        return [...result, widgetConfig];
      }
      return result;
    }, []);
  }, [state]);
  React.useEffect(() => {
    const script = createScriptTag({
      id: PR_SCRIPT_ID,
      src: PR_SCRIPT_SRC,
      defer: true,
      onError: () => {
        setHasError(true);
      }
    });
    injectTags([script]);
  }, []);
  React.useEffect(() => {
    if (enabled && widgets.length) {
      const mergedConfig = widgets.length > 1 ? mergeConfigs(widgets) : widgets[0];
      if (!mergedConfig) {
        return void 0;
      }
      removeDataLayerEventHandler();
      Object.values(mergedConfig.components).forEach((widgetId) => {
        clearWidgetHtml(widgetId);
      });
      window.pwr = window.pwr || function(...args) {
        (pwr.q = pwr.q || []).push(args);
      };
      window.pwr("render", mergedConfig);
      return () => {
        removeDataLayerEventHandler();
        Object.values(mergedConfig.components).forEach((widgetId) => {
          window.pwr("unmount", widgetId);
        });
      };
    }
  }, [widgets, enabled]);
  const value = React.useMemo(
    () => ({
      enabled,
      config,
      dispatch,
      reviewCount,
      hasError
    }),
    [enabled, config, reviewCount, hasError]
  );
  return /* @__PURE__ */ React.createElement(ReviewsContext.Provider, { value }, children);
};
function clearWidgetHtml(id) {
  const element = document.getElementById(id);
  if (element) {
    element.innerHTML = "";
  }
}
const removeDataLayerEventHandler = () => {
  const writeReviewLink = document.getElementsByClassName("pr-snippet-write-review-link")[0];
  writeReviewLink?.removeEventListener("click", pushWriteReviewToDataLayer);
};
const pushWriteReviewToDataLayer = () => {
  DataLayer.Events.pushDrawerEventToDataLayer({
    siteFeature: "reviews",
    interactionType: "write_first_review_click"
  });
};
export {
  PR_SCRIPT_ID,
  PR_SCRIPT_SRC,
  ReviewsProvider,
  ReviewsContext as default,
  mapStateToPowerReviewsType,
  useReviewsContext
};
//# sourceMappingURL=ReviewsContext.js.map
